import React from 'react';
import styled from 'styled-components';

const OurStoryContainer = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 20px;
`;

const MissionSection = styled.section`
  text-align: center;
  margin: 70px 0 60px;
`;

const MissionTitle = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 25px;
`;

const MissionText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #444;
  max-width: 800px;
  margin: 0 auto;
`;

const StartedSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 70px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StartedContent = styled.div``;

const StartedTitle = styled.h2`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const StartedText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #444;
  margin-bottom: 15px;
`;

const StartedImage = styled.div`
  background-color: #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ValuesSection = styled.section`
  background-color: #f8f9ff;
  padding: 60px 40px;
  border-radius: 12px;
  margin-bottom: 70px;
`;

const ValuesTitle = styled.h2`
  font-size: 32px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 40px;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  text-align: center;
`;

const ValueIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 5px;
  background-color: ${props => props.bgColor || '#e6f7ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const ValueTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
`;

const ValueText = styled.p`
  font-size: 16px;
  color: #555;
  line-height: 1.5;
`;

const JoinSection = styled.section`
  text-align: center;
  margin-bottom: 80px;
`;

const JoinTitle = styled.h2`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 25px;
`;

const JoinText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #444;
  max-width: 700px;
  margin: 0 auto 30px;
`;

const JourneyButton = styled.a`
  display: inline-block;
  background: linear-gradient(to right, #291759, #483D8B);
  color: white;
  font-size: 18px;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 30px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const OurStory = () => {
  return (
    <OurStoryContainer>
      <MissionSection>
        <MissionTitle>Our Mission</MissionTitle>
        <MissionText>
          Wonder is on a mission to revolutionize health management through
          personalized data, expert guidance, and community support.
        </MissionText>
      </MissionSection>
      
      <StartedSection>
        <StartedContent>
          <StartedTitle>How It All Started</StartedTitle>
          <StartedText>
            Wonder began in 2023 when our founder, Dr. Amanda Chen, recognized
            that many of her patients struggled to understand and act upon their
            health data in meaningful ways.
          </StartedText>
          <StartedText>
            After spending years in clinical practice, she saw firsthand how
            personalized nutrition and continuous glucose monitoring could
            transform lives when paired with the right support system.
          </StartedText>
          <StartedText>
            She assembled a team of nutritionists, software engineers, and health
            coaches to create a platform that makes complex health data
            accessible and actionable for everyone.
          </StartedText>
        </StartedContent>
        <StartedImage>
          <img src="https://via.placeholder.com/600x400" alt="How it all started" />
        </StartedImage>
      </StartedSection>
      
      <ValuesSection>
        <ValuesTitle>Our Core Values</ValuesTitle>
        <ValuesGrid>
          <ValueCard>
            <ValueIcon bgColor="#e6f7ff">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#0066cc">
                <rect x="2" y="2" width="9" height="9" rx="2" />
                <rect x="2" y="13" width="9" height="9" rx="2" />
                <rect x="13" y="2" width="9" height="9" rx="2" />
                <rect x="13" y="13" width="9" height="9" rx="2" />
              </svg>
            </ValueIcon>
            <ValueTitle>Evidence-Based Approach</ValueTitle>
            <ValueText>
              We ground all our recommendations and services in rigorous scientific
              research and clinical expertise.
            </ValueText>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon bgColor="#fff2e6">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#ff6600">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </ValueIcon>
            <ValueTitle>Personalization</ValueTitle>
            <ValueText>
              We recognize that every body is different, and customize our approach
              to your unique needs.
            </ValueText>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon bgColor="#e6ffec">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#00cc44">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </ValueIcon>
            <ValueTitle>Empowerment</ValueTitle>
            <ValueText>
              We equip you with the knowledge, tools, and support to take control of your
              health journey.
            </ValueText>
          </ValueCard>
        </ValuesGrid>
      </ValuesSection>
      
      <JoinSection>
        <JoinTitle>Join Our Mission</JoinTitle>
        <JoinText>
          We're building a community of individuals committed to better health through
          data-driven insights and expert guidance.
        </JoinText>
        <JourneyButton href="/auth/signup">START YOUR JOURNEY TODAY</JourneyButton>
      </JoinSection>
    </OurStoryContainer>
  );
};

export default OurStory; 